import api from "@/lib/axiosConfig.ts";
import { Author } from "@/lib/types";
import {
  keepPreviousData,
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { RelativePosition } from "../dtos";

export const baseRoute = "/back/api/authors";

interface AuthorDTO {
  where: any;
  order: any;
  take?: number;
  skip?: number;
}

async function getAuthors({
  where,
  order,
  take,
  skip,
}: AuthorDTO): Promise<Author[]> {
  const { data } = await api.post(
    "back/api/authors",
    {
      where,
      orderBy: order,
    },
    { params: { take, skip } },
  );
  return data;
}

export const useGetAllAuthors = ({ where, order, take, skip }: AuthorDTO) => {
  return useQuery({
    queryKey: ["authors", where, order, take, skip],
    queryFn: () => getAuthors({ where, order, take, skip }),
    placeholderData: keepPreviousData,
  });
};

async function getAuthor(id: string): Promise<Author> {
  const { data } = await api.get<Author>(`back/api/authors/${id}`);
  return data;
}

export const useGetAuthor = (id?: string) => {
  return useQuery({
    queryKey: ["author", id],
    queryFn: () => (id ? getAuthor(id) : undefined),
  });
};

export async function getCountByStatus(where?: object) {
  const { data } = await api.post(`back/api/authors/countByStatus`, { where });
  return data;
}

export const useCountByStatus = () => {
  return useQuery({
    queryKey: ["authors", "countByStatus"],
    queryFn: () => getCountByStatus(),
  });
};

export const getRelativePosition = async (
  id: string,
  order: object,
  where: object,
) => {
  const { data } = await api.post<RelativePosition>(
    `${baseRoute}/${id}/position`,
    {
      order,
      where,
    },
  );
  return data;
};

export const useGetAuthorsForMultiSelect = (
  type?: string,
  linkType?: string,
) => {
  return useQuery({
    queryKey: ["authors", "multiSelect"],
    queryFn: () =>
      getAuthors({
        where: {},
        order: { artistesAuteursDescriptionNom: "asc" },
      }),
    enabled:
      !!type && type === "link-simple" && !!linkType && linkType === "artist",
  });
};

async function updateAuthor(author: Author) {
  const {
    instanceId: _iid,
    updatedById: _ubid,
    createdById: _cbid,
    ...rest
  } = author;
  return await api.patch(`back/api/authors/${author.id}`, {
    data: rest,
  });
}

export function useUpdateAuthor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateAuthor,
    onSuccess: (_, v) => {
      queryClient.invalidateQueries({ queryKey: ["author", v.id] });
      queryClient.invalidateQueries({ queryKey: ["authors"] });
    },
  });
}

type CreateAuthorParams = {
  artistesAuteursDescriptionPrenom: string;
  artistesAuteursDescriptionNom: string;
};
type CreateAuthorResponse = Pick<Author, "id">;

const createAuthor = async ({
  artistesAuteursDescriptionPrenom,
  artistesAuteursDescriptionNom,
}: CreateAuthorParams) => {
  return await api
    .post<CreateAuthorResponse>("back/api/authors/create", {
      artistesAuteursDescriptionPrenom,
      artistesAuteursDescriptionNom,
    })
    .then((res) => res.data);
};

export const useCreateAuthor = (
  options?: UseMutationOptions<CreateAuthorResponse, Error, CreateAuthorParams>,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: createAuthor,
    onSuccess: (data, vars, ctx) => {
      queryClient.invalidateQueries({ queryKey: ["authors"] });
      queryClient.invalidateQueries({ queryKey: ["author", data.id] });
      queryClient.invalidateQueries({
        queryKey: ["authors", "countByStatus"],
      });
      queryClient.invalidateQueries({ queryKey: ["instance", "count-data"] });
      options?.onSuccess?.(data, vars, ctx);
    },
  });
};

export const getAllIds = async (where?: object) => {
  const { data } = await api.post<{ id: string }[]>(`${baseRoute}/ids`, {
    where,
  });
  return data;
};
